import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios, { CancelToken } from "axios";
import { useParams, useHistory } from "react-router";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import useModal from "hooks/modals/useModal";
import { displayPercent, valueToString } from "helpers/Helpers";
import { cloneDeep, isEqualWith, isNull, omit } from "lodash";
import moment from "moment";

import {
  GetInvoice,
  PutInvoice,
  GetInvoiceLineItem,
  PostInvoiceLineItem,
  PutInvoiceLineItem,
  DeleteInvoiceLineItem,
  GetDownloadInvoice,
  PutFinalizeInvoice,
  PutVoidInvoice,
  PostDuplicateInvoice,
  PutApplyDeposit,
  PutDeposit,
  GetInvoiceTransaction,
} from "api/rpc/2024-04/facilityAdmin/order/invoice";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { useAddFolderProductsPicker } from "hooks/folders/useAddFolderProductsPicker";
import { variantsFromSelectedProducts } from "pages/secure/facility/settings/teesheets/pricingSheets/pricingSheetApplications";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Popup from "components/popup/Popup";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { ButtonNew as Button, ButtonNew } from "components/buttonNew/index";
import { Badge } from "components/badge/Badge";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import InvoicePaymentModal from "./InvoicePaymentModal";
import InvoiceCustomerDetailsModal from "./InvoiceCustomerDetailsModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faPlus, IconName } from "@fortawesome/pro-solid-svg-icons";
import {
  faWallet,
  faVault,
  faSackDollar,
  faCheckCircle,
  faCircleInfo,
  faTurnDownRight,
  faXmark,
  faMoneyBill,
  faChevronUp,
  faChevronDown,
  faLock,
  faFilePlus,
  faPen,
} from "@fortawesome/pro-regular-svg-icons";
import { IInvoice, IInvoiceLineItem, IInvoiceTransaction } from "redux/reducers/models/order";
import { IProduct } from "redux/reducers/models/product";
import { IAddress } from "redux/reducers/models/customer";
import { loadPaymentOptions } from "redux/actions/facility";
import Portal from "elements/Portal";

import "./invoice.scss";
import { useTranslation } from "react-i18next";
import Divider from "components/divider";
import classNames from "classnames";
import TextEditor from "components/textEditor/textEditor";
import Markdown from "components/markdown/Markdown";
import InvoicePageSkeleton from "./InvoicePageSkeleton";
import { depositAmountToShow, visibleInvoiceDeposits } from "./invoiceDeposits";

interface IFilterState {
  eventQuery: string;
  selectedEvent: any;
  eventSearching: boolean;
}

interface IExtendedInvoiceLineItem extends IInvoiceLineItem {
  viewNote: boolean;
}

interface IRemoveLineItemState {
  modalVisible: boolean;
  itemId: number;
}

export default function InvoiceEdit() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const { Option } = Select;

  const [invoice, setInvoice] = useState<IInvoice>(undefined);
  const [parentInvoice, setParentInvoice] = useState<IInvoice>(undefined);
  const [invoiceBeforeChanges, setInvoiceBeforeChanges] = useState<IInvoice>(undefined);
  const [invoiceLineItems, setInvoiceLineItems] = useState<Array<IExtendedInvoiceLineItem>>(undefined);
  const [invoiceLineItemsBeforeChanges, setInvoiceLineItemsBeforeChanges] =
    useState<Array<IExtendedInvoiceLineItem>>(undefined);
  const [events, setEvents] = useState<Array<any>>(undefined);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [finalizePopupVisible, setFinalizePopupVisible] = useState(false);

  const [filterState, setFilterState] = useState<IFilterState>({
    eventQuery: "",
    selectedEvent: null,
    eventSearching: false,
  });

  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);

  const [removeLineItemState, setRemoveLineItemState] = useState<IRemoveLineItemState>({
    modalVisible: false,
    itemId: null,
  });

  const [addItemsOpen, setAddItemsOpen] = useState(false);
  const [originalItemsExpanded, setOriginalItemsExpanded] = useState(true);
  const addItemsPicker = useAddFolderProductsPicker({
    open: addItemsOpen,
    maxSelections: 200,
    includeVariants: true,
    onError: message => dispatch(showError(t(message))),
  });

  const {
    state: applyDepositPopup,
    updateModal: updateApplyDepositPopup,
    closeModal: closeApplyDepositPopup,
  } = useModal({ depositId: null });

  const {
    state: removeDepositPopup,
    updateModal: updateRemoveDepositPopup,
    closeModal: closeRemoveDepositPopup,
  } = useModal({ depositId: null });

  const {
    state: voidInvoicePopup,
    closeModal: closeVoidInvoicePopup,
    updateModal: updateVoidInvoicePopup,
  } = useModal();

  const {
    state: duplicateInvoicePopup,
    closeModal: closeDuplicateInvoicePopup,
    updateModal: updateDuplicateInvoicePopup,
  } = useModal();

  const {
    state: editDetailsModal,
    updateModal: updateEditDetailsModal,
    closeModal: closeEditDetailsModal,
  } = useModal<{
    date: string;
    invoice_due: string;
    register_id: number;
    po_number: string;
    notes: string;
  }>({
    date: "",
    invoice_due: "",
    register_id: null,
    po_number: "",
    notes: "",
  });

  useEffect(() => {
    void loadInvoice();
    void dispatch(loadPaymentOptions());
  }, []);

  useEffect(() => {
    if (!invoice || (!invoice?.tournament_id && !invoice.hospitality_event_id)) {
      const source = axios.CancelToken.source();
      void loadEvents(source.token);
      return () => {
        source.cancel();
      };
    }
  }, [filterState.eventQuery]);

  async function loadInvoice() {
    const invoiceRes = await GetInvoice(
      {
        id: Number(id),
        extended: true,
      },
      false,
    );

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.001")));
      return;
    }

    // If invoice has a parent invoice load it
    if (invoiceRes.data[0].parent_id) {
      const parentRes = await GetInvoice({ id: invoiceRes.data[0].parent_id }, false);

      if (invoiceRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.002")));
      } else {
        setParentInvoice(parentRes.data[0]);
      }
    }

    if (invoiceRes.data[0].tournament_id) {
      const tournamentRes = await GetTournament({ id: invoiceRes.data[0].tournament_id }, false);

      if (tournamentRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.003")));
        return;
      }

      invoiceRes.data[0].tournament = tournamentRes.data[0];

      const depositRes = await GetInvoiceTransaction({ tournament_id: tournamentRes.data[0].id }, false);

      if (depositRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.004")));
        return;
      }

      invoiceRes.data[0].tournament.deposits = depositRes.data;
    } else if (invoiceRes.data[0].hospitality_event_id) {
      const eventRes = await GetEvent({ id: invoiceRes.data[0].hospitality_event_id }, false);

      if (eventRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.005")));
        return;
      }

      invoiceRes.data[0].hospitality_event = eventRes.data[0];

      const depositRes = await GetInvoiceTransaction({ hospitality_event_id: eventRes.data[0].id }, false);

      if (depositRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.006")));
        return;
      }

      invoiceRes.data[0].hospitality_event.deposits = depositRes.data;
    }

    const lineItemRes = await GetInvoiceLineItem({ invoice_id: invoiceRes.data[0].id }, false);

    if (lineItemRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.007")));
      return;
    }

    // Keep both snapshots on the same shape so viewNote (UI-only) doesn't mark every row dirty
    const lineItemsWithViewNote = lineItemRes.data.map((lineItem: IInvoiceLineItem) => ({
      ...lineItem,
      viewNote: !!lineItem.note,
    }));

    const customerRes = await GetCustomer(
      {
        id: invoiceRes.data[0].customer_id,
        "address-lines": true,
      },
      false,
    );

    if (customerRes.data) {
      invoiceRes.data[0].customer = customerRes.data[0];
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setInvoice(invoiceRes.data[0]);
      setInvoiceBeforeChanges(invoiceRes.data[0]);
      setInvoiceLineItems(lineItemsWithViewNote);
      setInvoiceLineItemsBeforeChanges(lineItemsWithViewNote);
      setFilterState(prevState => ({ ...prevState, customerQuery: invoiceRes.data[0].customer?.full_name }));
    });
  }

  // viewNote is display-only; empty string and null are treated as the same blank value
  function invoiceValuesEqual(originalValue: unknown, newValue: unknown) {
    if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
      return true;
    }
  }

  function lineItemHasUnsavedChanges(
    originalLineItem: IExtendedInvoiceLineItem | undefined,
    lineItem: IExtendedInvoiceLineItem,
  ) {
    if (!originalLineItem) {
      return true;
    }

    return !isEqualWith(omit(originalLineItem, "viewNote"), omit(lineItem, "viewNote"), invoiceValuesEqual);
  }

  async function saveInvoice() {
    if (!isEqualWith(invoiceBeforeChanges, invoice, invoiceValuesEqual)) {
      const invoiceRes = await PutInvoice(
        {
          invoice_id: id,
          tournament_id: invoice.tournament ? invoice?.tournament_id : null,
          hospitality_event_id: invoice.hospitality_event ? invoice?.hospitality_event_id : null,
          customer_id: invoice?.customer_id,
          date: invoice?.date,
          invoice_due: invoice?.invoice_due,
          customer_billing_address_id: invoice?.customer_billing_address_id,
          customer_shipping_address_id: invoice?.customer_shipping_address_id,
          register_id: invoice?.register_id ?? undefined,
          notes: invoice?.notes,
          po_number: invoice?.po_number,
        },
        true,
      );

      if (invoiceRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.008")));
        return;
      }
    }

    const changedLineItems = invoiceLineItems.filter((lineItem, index) =>
      lineItemHasUnsavedChanges(invoiceLineItemsBeforeChanges[index], lineItem),
    );

    if (changedLineItems.length > 0) {
      const lineItemRes = await PutInvoiceLineItem(
        {
          line_items: changedLineItems.map(lineItem => ({
            id: lineItem.id,
            quantity: lineItem.quantity,
            price: lineItem.price,
            cost: lineItem.cost,
            note: lineItem.note,
          })),
        },
        true,
      );

      if (lineItemRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_edit.009")));
        return;
      }
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.010")));
    void loadInvoice();
  }

  async function loadEvents(token: CancelToken) {
    let tournaments: Array<any> = [];
    let events: Array<any> = [];
    setEvents(null);

    const tournamentRes = await GetTournament({ search: filterState.eventQuery }, false, token);

    if (token && token.reason) {
      return;
    }
    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.011")));
    } else {
      tournaments = tournamentRes.data.map(tournament => ({ ...tournament, event_type: "tournament" }));
    }

    const eventRes = await GetEvent({ search: filterState.eventQuery }, false, token);

    if (token && token.reason) {
      return;
    }
    if (eventRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.012")));
    } else {
      events = eventRes.data.map(event => ({ ...event, event_type: "hospitality" }));
    }

    const allEvents = tournaments.concat(events);

    setEvents(allEvents);
  }

  async function downloadInvoice() {
    const invoiceRes = await GetDownloadInvoice({ invoice_id: Number(id) }, true);

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.013")));
      return;
    }

    window.open(String(invoiceRes.data));
  }

  async function postLineItem(products: IProduct[]) {
    const selectedVariants = variantsFromSelectedProducts(products);
    const selectedVariantIds = selectedVariants
      .map(variant => variant.id)
      .filter((variantId): variantId is number => variantId != null);

    if (selectedVariantIds.length === 0) {
      dispatch(showError(t("A product must be selected")));
      return;
    }

    const lineItemRes = await PostInvoiceLineItem(
      {
        invoice_id: Number(id),
        variants: selectedVariantIds.map(variantId => ({ variant_id: variantId, quantity: 1 })),
      },
      true,
    );

    if (lineItemRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.014")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.015")));
    setAddItemsOpen(false);
    addItemsPicker.resetPicker();
    void loadInvoice();
  }

  async function removeLineItem(id: number) {
    const removeRes = await DeleteInvoiceLineItem({ id: id }, true);

    if (removeRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.016")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.017")));
    setRemoveLineItemState({ modalVisible: false, itemId: null });
    void loadInvoice();
  }

  async function finalizeInvoice() {
    const filteredGiftCards: any[] = invoiceLineItems?.filter(
      (line_item: Record<string, any>) => line_item?.product?.type === "Gift Card",
    );

    const gift_cards: any[] = [];

    if (filteredGiftCards?.length > 0) {
      //Check for gift cards
      if (filteredGiftCards.length > 0) {
        filteredGiftCards.forEach((line_item: Record<string, any>) => {
          for (let i = 0; i < line_item?.quantity; i++) {
            gift_cards.push({
              product_id: line_item.product_id,
              cart_line_item_id: line_item.id,
              code: null,
              pin: null,
              random: true,
              balance: line_item.price,
              reload: 0,
              gift_card_restrict_facility: line_item.product?.gift_card_restrict_facility,
              gift_card_expiry_type: line_item.product?.gift_card_expiry_type,
              gift_card_expiry_years: line_item.product?.gift_card_expiry_years,
            });
          }
        });
      }
    }

    const params = {
      invoice_id: Number(id),
      gift_cards: gift_cards,
    };

    const finalizeRes = await PutFinalizeInvoice(params, true);

    if (finalizeRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.018")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.019")));
    setFinalizePopupVisible(false);
    history.push("/admin/invoices/" + id);
  }

  async function applyDeposit(depositId: number) {
    if (!depositId) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.020")));
      return;
    }

    const applyRes = await PutApplyDeposit(
      {
        invoice_id: Number(id),
        deposit_id: depositId,
      },
      true,
    );

    if (applyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.021")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.022")));
    updateApplyDepositPopup({ isOpen: false, depositId: null });
    void loadInvoice();
  }

  async function removeDeposit(depositId: number) {
    if (!depositId) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.023")));
      return;
    }

    const applyRes = await PutDeposit(
      {
        invoice_id: null,
        deposit_id: depositId,
      },
      true,
    );

    if (applyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.024")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.025")));
    updateRemoveDepositPopup({ isOpen: false, depositId: null });
    void loadInvoice();
  }

  async function voidInvoice() {
    const voidRes = await PutVoidInvoice({ invoice_id: Number(id) }, true);

    if (voidRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.026")));
      return;
    }

    void loadInvoice();
    updateVoidInvoicePopup({ isOpen: false });
  }

  async function duplicateInvoice() {
    const duplicateRes = await PostDuplicateInvoice({ invoice_id: Number(id) }, true);

    if (duplicateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_edit.027")));
      return;
    }

    updateDuplicateInvoicePopup({ isOpen: false });
    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_edit.028")));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>, lineItemIndex: number) {
    const { id, value } = e.target;

    let updatedLineItem = cloneDeep(invoiceLineItems?.[lineItemIndex]);

    if (!updatedLineItem) {
      return;
    }

    updatedLineItem = {
      ...updatedLineItem,
      [id]: value,
    };

    const updatedInvoiceLineItems = cloneDeep(invoiceLineItems);

    updatedInvoiceLineItems[lineItemIndex] = updatedLineItem;

    setInvoiceLineItems(updatedInvoiceLineItems);
  }

  function toggleAddNote(lineItemIndex: number, checked: boolean) {
    let updatedLineItem = cloneDeep(invoiceLineItems?.[lineItemIndex]);

    updatedLineItem = {
      ...updatedLineItem,
      viewNote: checked,
    };

    const updatedInvoiceLineItems = cloneDeep(invoiceLineItems);

    updatedInvoiceLineItems[lineItemIndex] = updatedLineItem;

    setInvoiceLineItems(updatedInvoiceLineItems);
  }

  function handleShowCustomerDetails() {
    if (invoice?.financial_status !== "draft") {
      return;
    }

    setCustomerDetailsOpen(true);
  }

  function saveCustomerDetailsFromModal(next: {
    customer: IInvoice["customer"];
    billingAddress: IAddress | null;
    shippingAddress: IAddress | null;
  }) {
    setInvoice(
      prev =>
        ({
          ...prev,
          customer: next.customer,
          customer_id: next.customer?.id,
          billing_address: next.billingAddress,
          shipping_address: next.shippingAddress,
          customer_billing_address_id: next.billingAddress?.id,
          customer_shipping_address_id: next.shippingAddress?.id,
        }) as IInvoice,
    );
    setCustomerDetailsOpen(false);
  }

  function handleEditEvent() {
    const updatedInvoice = cloneDeep(invoice);

    updatedInvoice.tournament = null;
    updatedInvoice.tournament_id = null;
    updatedInvoice.hospitality_event = null;
    updatedInvoice.hospitality_event_id = null;

    setInvoice(updatedInvoice);
  }

  function handleEventChange(event?: any) {
    const updatedInvoice = cloneDeep(invoice);

    if (event.event_type === "tournament") {
      updatedInvoice.tournament_id = event.id;
      updatedInvoice.tournament = event;
    } else if (event.event_type === "hospitality") {
      updatedInvoice.hospitality_event_id = event.id;
      updatedInvoice.hospitality_event = event;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setInvoice(updatedInvoice);

      setFilterState(prevState => ({
        ...prevState,
        tournamentQuery: "",
        selectedTournament: null,
      }));
    });
  }

  function navigateToParentInvoice() {
    const url =
      parentInvoice?.financial_status === "draft"
        ? `/admin/invoices/${parentInvoice.id}/edit`
        : `/admin/invoices/${parentInvoice.id}`;

    history.push(url);
  }

  function unsavedChangesExist() {
    return (
      !isEqualWith(invoiceBeforeChanges, invoice, invoiceValuesEqual) ||
      !!invoiceLineItems?.some((lineItem, index) =>
        lineItemHasUnsavedChanges(invoiceLineItemsBeforeChanges?.[index], lineItem),
      )
    );
  }

  function getDepositsApplied(): number {
    if (!invoice?.transactions) {
      return 0;
    }

    const depositIds = new Set(
      invoice.transactions.filter(transaction => transaction.kind === "deposit").map(transaction => transaction.id),
    );

    let sum = 0;
    for (const transaction of invoice.transactions) {
      if (transaction.kind === "deposit") {
        sum += Number(transaction.amount) || 0;
      } else if (transaction.kind === "refund" && depositIds.has(Number(transaction.parent_id))) {
        sum -= Number(transaction.amount) || 0;
      }
    }
    return sum;
  }

  function getBalance(): number {
    if (!invoice?.transactions) {
      return invoice?.total_price ?? 0;
    }

    let total = invoice.total_price;
    for (const transaction of invoice.transactions) {
      if (transaction.kind === "refund" || transaction.kind === "invoice_debit_adjustment") {
        total += transaction.amount;
      } else {
        total -= transaction.amount;
      }
    }
    return total;
  }

  function formatCurrency(amount: number) {
    return Intl.NumberFormat("en-CA", { style: "currency", currency: invoice?.currency || "CAD" }).format(amount);
  }

  function openEditDetailsModal() {
    updateEditDetailsModal({
      isOpen: true,
      date: invoice?.date || "",
      invoice_due: invoice?.invoice_due || "",
      register_id: invoice?.register_id || null,
      po_number: invoice?.po_number || "",
      notes: invoice?.notes || "",
    });
  }

  function calculateDueDate(date: string, invoiceDue: string) {
    if (!date) {
      return "";
    }

    const invoiceDate = moment(date);
    if (invoiceDue === "net_30") {
      return invoiceDate.add(30, "days").format("YYYY-MM-DD");
    }
    if (invoiceDue === "net_60") {
      return invoiceDate.add(60, "days").format("YYYY-MM-DD");
    }

    return invoiceDate.format("YYYY-MM-DD");
  }

  function getPaymentTermsLabel(invoiceDue?: string | null) {
    switch (invoiceDue) {
      case "on_receipt":
        return t("On Receipt");
      case "net_30":
        return t("Net 30");
      case "net_60":
        return t("Net 60");
      default:
        return null;
    }
  }

  function getRegisterTitle(registerId?: number | null) {
    if (!registerId) {
      return null;
    }

    return (
      invoice?.register?.title ||
      facilityStore?.registers?.find(register => register.id === registerId)?.title ||
      null
    );
  }

  function saveInvoiceDetailsFromModal() {
    const selectedRegister = facilityStore?.registers?.find(
      register => register.id === editDetailsModal.register_id,
    );

    setInvoice(prev => ({
      ...prev,
      date: editDetailsModal.date,
      invoice_due: editDetailsModal.invoice_due,
      due_date: calculateDueDate(editDetailsModal.date, editDetailsModal.invoice_due),
      register_id: editDetailsModal.register_id,
      register: selectedRegister ?? prev.register,
      po_number: editDetailsModal.po_number,
      notes: editDetailsModal.notes,
    }));
    closeEditDetailsModal();
  }

  function getEventDeposits() {
    return invoice?.tournament?.deposits || invoice?.hospitality_event?.deposits || [];
  }

  function cancelUnsavedChanges() {
    setInvoice(invoiceBeforeChanges);
    setInvoiceLineItems(invoiceLineItemsBeforeChanges);
  }

  if (invoice === undefined || invoiceLineItems === undefined) {
    return (
      <Page
        title={t("secure.facility.order.invoices.invoice_edit.029")}
        breadcrumbs={[
          { prefix: true, label: t("secure.facility.order.invoices.invoice_edit.030"), url: "/admin/invoices" },
        ]}
        splitLayout
      >
        <InvoicePageSkeleton
          customerTitle={t("Bill To")}
          itemsTitle={t("secure.facility.order.invoices.invoice_edit.044")}
          sidebarTitle={t("secure.facility.order.invoices.invoice_edit.054")}
          editable
        />
      </Page>
    );
  }

  const depositsApplied = getDepositsApplied();
  const balance = getBalance();
  const eventDeposits = getEventDeposits();
  const hasEvent = !!(invoice?.tournament || invoice?.hospitality_event);
  // Event deposits are loaded for the invoice's tournament/hospitality event.
  // Show ones applied to this invoice and ones still unapplied (invoice_id null),
  // including a deposit that was only partially refunded.
  // Deposits already applied to a different invoice stay hidden.
  const visibleDeposits = visibleInvoiceDeposits(eventDeposits, invoice?.id);
  const visiblePayments =
    invoice?.transactions?.filter(
      transaction =>
        transaction.kind !== "deposit" &&
        transaction.kind !== "deposit_authorization" &&
        transaction.kind !== "refund",
    ) ?? [];
  const hasNoPaymentActivity = visibleDeposits.length === 0 && visiblePayments.length === 0;
  const lockedLineItems = invoiceLineItems?.filter(item => item.locked) ?? [];
  const unlockedLineItems = invoiceLineItems?.filter(item => !item.locked) ?? [];
  const paymentTermsLabel = getPaymentTermsLabel(invoice?.invoice_due);

  function renderLineItemCard(lineItem: IExtendedInvoiceLineItem, index: number) {
    const isLocked = Boolean(lineItem.locked);

    return (
      <div key={lineItem.id} className="invoice-item-card">
        <div className="invoice-line-item-redesign">
          <div className="invoice-line-item-redesign__header">
            <div className="invoice-line-item-redesign__info">
              <div className="invoice-line-item-redesign__title-row">
                <p className="text-md text-semibold">{lineItem.product_title}</p>
                {isLocked ? (
                  <span className="invoice-line-item-redesign__lock-badge" aria-hidden>
                    <FontAwesomeIcon icon={faLock} className="invoice-line-item-redesign__lock-icon" />
                  </span>
                ) : null}
              </div>
              {lineItem.product_title !== lineItem.variant_title ? (
                <p className="text-sm text-gray-500">{lineItem.variant_title}</p>
              ) : null}
            </div>
            <div className="invoice-line-item-redesign__actions">
              {isLocked ? (
                <button
                  type="button"
                  className="invoice-line-item-redesign__note-button"
                  onClick={() => toggleAddNote(index, !lineItem.viewNote)}
                  aria-label={t("Add a note")}
                  title={t("Add a note")}
                >
                  <FontAwesomeIcon icon={faFilePlus} />
                </button>
              ) : (
                <NavigationDropdownNew
                  showPlainTextLabel
                  rightAlign
                  label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                  sections={[
                    [
                      {
                        label: t("Remove item"),
                        customIcon: <FontAwesomeIcon icon={faXmark} />,
                        color: "#B42318",
                        onClick: () => setRemoveLineItemState({ modalVisible: true, itemId: lineItem.id }),
                      },
                    ],
                    [
                      {
                        label: t("Add a note"),
                        customIcon: <FontAwesomeIcon icon={faFilePlus} />,
                        onClick: () => toggleAddNote(index, !lineItem.viewNote),
                      },
                    ],
                  ]}
                />
              )}
            </div>
          </div>
          <div className="invoice-line-item-redesign__inputs">
            <div className="invoice-line-item-redesign__input-group">
              <Input
                value={lineItem?.quantity}
                label={t("secure.facility.order.invoices.invoice_edit.047")}
                id={"quantity"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, index)}
                type="number"
                min={1}
                disabled={isLocked}
                readOnly={isLocked}
              />
            </div>
            <div className="invoice-line-item-redesign__input-group">
              <Input
                value={lineItem?.price}
                label={t("secure.facility.order.invoices.invoice_edit.048")}
                id={"price"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, index)}
                type="number"
                prefix={"$"}
                min={0}
                disabled={isLocked}
                readOnly={isLocked}
              />
            </div>
            <div className="invoice-line-item-redesign__input-group">
              <Input
                value={lineItem?.cost}
                label={t("Cost")}
                id={"cost"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, index)}
                type="number"
                prefix={"$"}
                min={0}
                disabled={isLocked}
                readOnly={isLocked}
              />
            </div>
            <div className="invoice-line-item-redesign__total">
              <p className="text-md text-semibold">
                {formatCurrency(Number(lineItem?.price * lineItem?.quantity))}
              </p>
            </div>
          </div>
        </div>
        {lineItem.viewNote && (
          <Input
            value={lineItem?.note}
            id={"note"}
            placeholder={t("secure.facility.order.invoices.invoice_edit.053")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, index)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <Page
        title={`${t("secure.facility.order.invoices.invoice_edit.029")}${invoice?.name}`}
        titleMetadata={
          <Badge type="primary" outline className="invoice-status-badge--draft" iconLeft={<FontAwesomeIcon icon={faPen} />}>
            Draft
          </Badge>
        }
        breadcrumbs={[
          { prefix: true, label: t("secure.facility.order.invoices.invoice_edit.030"), url: "/admin/invoices" },
        ]}
        splitLayout
        className="invoice-details-page"
        belowHeaderContent={
          <Card className="invoice-summary-card">
            <div className="invoice-summary-stats">
              <div className="invoice-summary-stat">
                <div className="invoice-summary-stat__icon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div className="invoice-summary-stat__content">
                  <p className="invoice-summary-stat__value">{formatCurrency(invoice?.total_price)}</p>
                  <p className="invoice-summary-stat__label">{t("Total")}</p>
                </div>
              </div>
              <div className="invoice-summary-stat__divider" />
              <div className="invoice-summary-stat">
                <div className="invoice-summary-stat__icon">
                  <FontAwesomeIcon icon={faVault} />
                </div>
                <div className="invoice-summary-stat__content">
                  <p className="invoice-summary-stat__value">
                    {depositsApplied > 0 ? `-${formatCurrency(depositsApplied)}` : formatCurrency(0)}
                  </p>
                  <p className="invoice-summary-stat__label">{t("Deposits applied")}</p>
                </div>
              </div>
              <div className="invoice-summary-stat__divider" />
              <div className="invoice-summary-stat">
                <div className="invoice-summary-stat__icon">
                  <FontAwesomeIcon icon={faSackDollar} />
                </div>
                <div className="invoice-summary-stat__content">
                  <p className="invoice-summary-stat__value">{formatCurrency(balance)}</p>
                  <p className="invoice-summary-stat__label">
                    {t("Balance due")}
                    {paymentTermsLabel ? ` • ${paymentTermsLabel}` : ""}
                    {invoice?.due_date ? ` • ${t("Due on")} ${invoice.due_date}` : ""}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        }
        multipleActionDropdownAction={{
          label: t("secure.facility.order.invoices.invoice_edit.031"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("secure.facility.order.invoices.invoice_edit.032"),
                icon: "money-bill",
                handler: () => setDepositModalVisible(true),
              },
              {
                type: "handler",
                label: t("Make Payment"),
                icon: "money-check-dollar",
                handler: () => setPaymentModalVisible(true),
              },
              {
                type: "handler",
                label: t("secure.facility.order.invoices.invoice_edit.033"),
                icon: "download",
                handler: () => void downloadInvoice(),
              },
              {
                type: "handler",
                label: t("secure.facility.order.invoices.invoice_edit.034"),
                icon: "check",
                handler: () => setFinalizePopupVisible(true),
                disabled: invoice?.financial_status !== "draft",
              },
              {
                type: "handler",
                label: t("secure.facility.order.invoices.invoice_edit.035"),
                icon: "ban",
                handler: () => updateVoidInvoicePopup({ isOpen: true }),
                disabled: invoice?.financial_status === "paid" || invoice?.financial_status === "partially_paid",
              },
              ...(invoice?.parent_id
                ? [
                    {
                      type: "handler" as const,
                      label: t("View original invoice"),
                      icon: "arrow-up-right-from-square" as IconName,
                      handler: () => navigateToParentInvoice(),
                    },
                  ]
                : []),
              {
                type: "handler",
                label: t("secure.facility.order.invoices.invoice_edit.036"),
                icon: "copy",
                handler: () => updateDuplicateInvoicePopup({ isOpen: true }),
                disabled: invoice?.financial_status === "voided",
              },
            ],
          },
        }}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: () => void saveInvoice(),
          onCancel: cancelUnsavedChanges,
        }}
      >
        <Page.Section twoThirds>
          <Card
            title={t("Bill to")}
            titleActions={[
              {
                content: t("Edit"),
                action: () => handleShowCustomerDetails(),
              },
            ]}
          >
            <Divider />
            <Card.Section>
              {invoice?.customer ? (
                <div className="invoice-bill-to">
                  <div className="invoice-bill-to__column">
                    <p className="text-md text-semibold">{invoice?.customer?.full_name}</p>
                    <p className="text-md text-gray-500">{invoice?.customer?.email}</p>
                    <p className="text-md text-gray-500">
                      {invoice?.customer?.phone ? invoice?.customer?.phone : t("No phone number attached")}
                    </p>
                  </div>
                  <div className="invoice-bill-to__column">
                    <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice_edit.039")}</p>
                    {invoice?.billing_address ? (
                      <>
                        <p className="text-md text-gray-500">{invoice?.billing_address?.address_line_1}</p>
                        {invoice?.billing_address?.address_line_2 && (
                          <p className="text-md text-gray-500">{invoice.billing_address.address_line_2}</p>
                        )}
                        <p className="text-md text-gray-500">{`${invoice?.billing_address?.city}, ${invoice?.billing_address?.province_code}`}</p>
                        <p className="text-md text-gray-500">{invoice?.billing_address?.postal}</p>
                      </>
                    ) : (
                      <p className="text-md text-gray-500">{t("secure.facility.order.invoices.invoice_edit.041")}</p>
                    )}
                  </div>
                  <div className="invoice-bill-to__column">
                    <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice_edit.042")}</p>
                    {invoice?.shipping_address ? (
                      <>
                        <p className="text-md text-gray-500">{invoice?.shipping_address?.address_line_1}</p>
                        {invoice?.shipping_address?.address_line_2 && (
                          <p className="text-md text-gray-500">{invoice.shipping_address.address_line_2}</p>
                        )}
                        <p className="text-md text-gray-500">{`${invoice?.shipping_address?.city}, ${invoice?.shipping_address?.province_code}`}</p>
                        <p className="text-md text-gray-500">{invoice?.shipping_address?.postal}</p>
                      </>
                    ) : (
                      <p className="text-md text-gray-500">{t("secure.facility.order.invoices.invoice_edit.041")}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-lg">{t("secure.facility.order.invoices.invoice_edit.043")}</p>
              )}
            </Card.Section>
          </Card>

          <Card
            title={`${t("secure.facility.order.invoices.invoice_edit.044")} (${invoiceLineItems?.length ?? 0})`}
            titleActions={[
              {
                content: `+ ${t("Add items")}`,
                action: () => setAddItemsOpen(true),
                disabled: invoice?.financial_status !== "draft",
              },
            ]}
          >
            <Divider />
            <Card.Section>
              <div className="invoice-items-list">
                {lockedLineItems.length > 0 && (
                  <div className="invoice-original-items">
                    <button
                      type="button"
                      className="invoice-original-items__toggle"
                      onClick={() => setOriginalItemsExpanded(prev => !prev)}
                    >
                      <div className="invoice-original-items__copy">
                        <p className="invoice-original-items__title">
                          {t("Original invoice items")} ({lockedLineItems.length})
                        </p>
                        {originalItemsExpanded ? (
                          <p className="invoice-original-items__description">
                            {t("View locked items from the voided invoice {{name}} or add a note.", {
                              name: parentInvoice?.name ?? invoice?.name ?? "",
                            })}
                          </p>
                        ) : null}
                      </div>
                      <FontAwesomeIcon
                        icon={originalItemsExpanded ? faChevronUp : faChevronDown}
                        className="invoice-original-items__chevron"
                      />
                    </button>
                    {originalItemsExpanded ? (
                      <div className="invoice-original-items__list">
                        {lockedLineItems.map(lineItem => {
                          const index = invoiceLineItems.findIndex(item => item.id === lineItem.id);
                          return renderLineItemCard(lineItem, index);
                        })}
                      </div>
                    ) : null}
                  </div>
                )}

                {unlockedLineItems.map(lineItem => {
                  const index = invoiceLineItems.findIndex(item => item.id === lineItem.id);
                  return renderLineItemCard(lineItem, index);
                })}
              </div>
            </Card.Section>
          </Card>
        </Page.Section>

        <Page.Section oneThird>
          <Card title={t("Payment summary")}>
            <Divider />
            <Card.Section>
              <div className="invoice-payment-summary">
                <div className="invoice-payment-summary__row">
                  <span>{t("secure.facility.order.invoices.invoice_edit.101")}</span>
                  <span className="invoice-payment-summary__dots" />
                  <span className="text-semibold">{formatCurrency(invoice?.subtotal_price)}</span>
                </div>
                {invoice?.tax_lines?.map((line, index) => (
                  <div key={index} className="invoice-payment-summary__row">
                    <span>
                      {line.title}{" "}
                      <span className="text-gray-500">{displayPercent(Number(line.rate), 3)}</span>
                    </span>
                    <span className="invoice-payment-summary__dots" />
                    <span>{formatCurrency(Number(line?.price))}</span>
                  </div>
                ))}
                {invoice?.total_tip > 0 ? (
                  <div className="invoice-payment-summary__row">
                    <span>{t("secure.facility.order.invoices.invoice_edit.071")}</span>
                    <span className="invoice-payment-summary__dots" />
                    <span>{formatCurrency(Number(invoice?.total_tip))}</span>
                  </div>
                ) : null}
                <div className="invoice-payment-summary__row">
                  <span className="text-semibold">{t("secure.facility.order.invoices.invoice_edit.072")}</span>
                  <span className="invoice-payment-summary__dots" />
                  <span className="text-semibold">{formatCurrency(invoice?.total_price)}</span>
                </div>

                {invoice?.transactions?.some(txn => txn.kind === "deposit") && (
                  <>
                    <p className="invoice-payment-summary__section-label">{t("Deposits")}</p>
                    <Divider />
                    {invoice?.transactions
                      ?.filter(transaction => transaction.kind === "deposit")
                      ?.map((transaction: IInvoiceTransaction, index: number) => (
                        <div key={`dep-${index}`} className="invoice-payment-summary__row">
                          <span>{valueToString(transaction.payment_type)}</span>
                          <span className="invoice-payment-summary__dots" />
                          <span>{formatCurrency(depositAmountToShow(transaction, invoice?.transactions ?? []))}</span>
                        </div>
                      ))}
                  </>
                )}

                {invoice?.transactions?.some(
                  txn => txn.kind !== "deposit" && txn.kind !== "deposit_authorization" && txn.kind !== "refund",
                ) && (
                  <>
                    <p className="invoice-payment-summary__section-label">{t("Payments")}</p>
                    <Divider />
                    {invoice?.transactions
                      ?.filter(
                        transaction =>
                          transaction.kind !== "deposit" &&
                          transaction.kind !== "deposit_authorization" &&
                          transaction.kind !== "refund",
                      )
                      ?.map((transaction: IInvoiceTransaction, index: number) => {
                        const isRefunded = transaction.refunded;
                        return (
                          <React.Fragment key={`pay-${index}`}>
                            <div className="invoice-payment-summary__row">
                              <span>{valueToString(transaction.payment_type)}</span>
                              <span className="invoice-payment-summary__dots" />
                              <span>
                                {formatCurrency(
                                  transaction.kind === "invoice_debit_adjustment"
                                    ? transaction.amount * -1
                                    : transaction.amount,
                                )}
                              </span>
                            </div>
                            {isRefunded && (
                              <div className="invoice-payment-summary__refund-row">
                                <FontAwesomeIcon icon={faTurnDownRight} className="invoice-payment-summary__refund-icon" />
                                <span className="invoice-payment-summary__refund-label text-semibold">
                                  {t("Refunded")}
                                </span>
                                <span className="invoice-payment-summary__dots" />
                                <span>{formatCurrency(transaction.amount * -1)}</span>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </>
                )}

                <Divider />
                <div className="invoice-payment-summary__row invoice-payment-summary__row--total">
                  <span className="text-semibold" style={{ fontSize: "16px" }}>
                    {t("secure.facility.order.invoices.invoice_edit.074")}
                  </span>
                  <span className="invoice-payment-summary__dots" />
                  <span className={classNames({ "invoice-balance-due": balance > 0 }, "text-semibold")} style={{ fontSize: "16px", lineHeight: "24px" }}>
                    {formatCurrency(balance)}
                  </span>
                </div>
              </div>
            </Card.Section>
          </Card>

          <Card title={t("Payment activity")}>
            <Divider />
            <Card.Section>
              <div className="invoice-payment-activity">
                {hasNoPaymentActivity ? (
                  <div className="invoice-payment-activity__empty">
                    <div className="invoice-payment-activity__empty-icon">
                      <FontAwesomeIcon icon={faMoneyBill} />
                    </div>
                    <p className="invoice-payment-activity__empty-title">{t("No payment activity yet")}</p>
                    <p className="invoice-payment-activity__empty-description">
                      {t("Add a deposit or make a payment to record money received for this invoice.")}
                    </p>
                    <div className="invoice-payment-activity__empty-actions">
                      <Button type="secondary" size="medium" onClick={() => setDepositModalVisible(true)}>
                        {t("Add deposit")}
                      </Button>
                      <Button type="primary" size="medium" onClick={() => setPaymentModalVisible(true)}>
                        {t("Make payment")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {hasEvent &&
                      (visibleDeposits.length > 0 ? (
                        <>
                          {visibleDeposits.map((deposit, index) => {
                            const isApplied = deposit?.invoice_id !== null;
                            return (
                              <div
                                key={deposit?.id}
                                className={classNames("invoice-payment-activity__item", {
                                  "invoice-payment-activity__item--applied": isApplied,
                                })}
                              >
                                <div
                                  className={classNames("invoice-payment-activity__icon", {
                                    "invoice-payment-activity__icon--success": isApplied,
                                    "invoice-payment-activity__icon--default": !isApplied,
                                  })}
                                >
                                  <FontAwesomeIcon icon={isApplied ? faCheckCircle : faCircleInfo} size="sm" />
                                </div>
                                <div className="invoice-payment-activity__content">
                                  <span className="invoice-payment-activity__title">
                                    {t("secure.facility.order.invoices.invoice_edit.065")} {index + 1}
                                  </span>
                                  <span className="invoice-payment-activity__subtitle">
                                    {formatCurrency(depositAmountToShow(deposit, eventDeposits))} • {valueToString(deposit?.payment_type)}
                                  </span>
                                </div>
                                <div className="invoice-payment-activity__action">
                                  {isApplied ? (
                                    <Button
                                      type="text"
                                      size="small"
                                      onClick={() =>
                                        updateRemoveDepositPopup({ isOpen: true, depositId: deposit?.id })
                                      }
                                    >
                                      <span
                                        className="invoice-text-error"
                                        style={{ fontWeight: 600, fontSize: "12px" }}
                                      >
                                        {t("Remove")}
                                      </span>
                                    </Button>
                                  ) : (
                                    <Button
                                      type="secondary"
                                      size="small"
                                      onClick={() =>
                                        updateApplyDepositPopup({ isOpen: true, depositId: deposit?.id })
                                      }
                                    >
                                      {t("Apply")}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="invoice-payment-activity__add-deposit"
                            onClick={() => setDepositModalVisible(true)}
                          >
                            <FontAwesomeIcon icon={faPlus} size="sm" />
                            {t("Add deposit")}
                          </button>
                        </>
                      ) : (
                        <div className="invoice-payment-activity__empty">
                          <div className="invoice-payment-activity__empty-icon">
                            <FontAwesomeIcon icon={faMoneyBill} />
                          </div>
                          <p className="invoice-payment-activity__empty-title">{t("No deposits yet")}</p>
                          <p className="invoice-payment-activity__empty-description">
                            {t("There aren't any deposits attached to this invoice yet")}
                          </p>
                          <Button
                            type="primary"
                            size="medium"
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => setDepositModalVisible(true)}
                          >
                            {t("Add deposit")}
                          </Button>
                        </div>
                      ))}

                    {hasEvent && <Divider />}

                    {visiblePayments.length > 0 ? (
                      visiblePayments.map((transaction: IInvoiceTransaction, index: number) => (
                        <div
                          key={transaction?.id ?? `payment-${index}`}
                          className="invoice-payment-activity__item"
                        >
                          <div className="invoice-payment-activity__icon invoice-payment-activity__icon--default">
                            <FontAwesomeIcon icon={faCircleInfo} size="sm" />
                          </div>
                          <div className="invoice-payment-activity__content">
                            <span className="invoice-payment-activity__title">
                              {t("Payment")} {index + 1}
                            </span>
                            <span className="invoice-payment-activity__subtitle">
                              {formatCurrency(transaction?.amount)} • {valueToString(transaction?.payment_type)}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="invoice-payment-activity__empty">
                        <div className="invoice-payment-activity__empty-icon">
                          <FontAwesomeIcon icon={faMoneyBill} />
                        </div>
                        <p className="invoice-payment-activity__empty-title">{t("No payments made yet")}</p>
                        <p className="invoice-payment-activity__empty-description">
                          {t("There aren't any payments made to this invoice yet")}
                        </p>
                        <Button
                          type="primary"
                          size="medium"
                          icon={<FontAwesomeIcon icon={faPlus} />}
                          onClick={() => setPaymentModalVisible(true)}
                        >
                          {t("Make payment")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card.Section>
          </Card>

          <Card
            title={t("Invoice Details")}
            titleActions={[{ content: t("Edit"), action: openEditDetailsModal }]}
          >
            <Card.Section>
              <div className="invoice-details-grid">
                <div className="invoice-details-grid__row">
                  <span className="invoice-details-grid__label">{t("Invoice Date")}</span>
                  <span className="invoice-details-grid__value">{invoice?.date || "-"}</span>
                </div>
                <div className="invoice-details-grid__row">
                  <span className="invoice-details-grid__label">{t("Due Date")}</span>
                  <span className="invoice-details-grid__value invoice-details-grid__value--stack">
                    <span>{invoice?.due_date || "-"}</span>
                    {paymentTermsLabel ? (
                      <span className="invoice-details-grid__value--secondary">{paymentTermsLabel}</span>
                    ) : null}
                  </span>
                </div>
                <div className="invoice-details-grid__row">
                  <span className="invoice-details-grid__label">{t("Register")}</span>
                  <span
                    className={classNames("invoice-details-grid__value", {
                      "invoice-details-grid__value--empty": !getRegisterTitle(invoice?.register_id),
                    })}
                  >
                    {getRegisterTitle(invoice?.register_id) || "-"}
                  </span>
                </div>
                <div className="invoice-details-grid__row">
                  <span className="invoice-details-grid__label">{t("PO Number")}</span>
                  <span
                    className={classNames("invoice-details-grid__value", {
                      "invoice-details-grid__value--empty": !invoice?.po_number,
                    })}
                  >
                    {invoice?.po_number || "-"}
                  </span>
                </div>
                <div className="invoice-details-grid__row">
                  <span className="invoice-details-grid__label">{t("Note")}</span>
                  <span
                    className={classNames("invoice-details-grid__value invoice-details-grid__value--note", {
                      "invoice-details-grid__value--empty": !invoice?.notes,
                    })}
                  >
                    {invoice?.notes ? <Markdown markdownText={invoice.notes} /> : "-"}
                  </span>
                </div>
              </div>
            </Card.Section>
          </Card>

          <Card
            title={t("Event")}
            titleActions={
              invoice?.tournament || invoice?.hospitality_event
                ? [{ content: t("Edit"), action: () => handleEditEvent() }]
                : []
            }
          >
            <Divider />
            <Card.Section>
              {invoice?.tournament || invoice?.hospitality_event ? (
                invoice?.tournament ? (
                  <div>
                    <p className="text-md text-semibold">{invoice?.tournament?.name}</p>
                    <p className="text-sm text-gray-500">
                      {`${invoice?.tournament?.total_participant_count}/${Number(invoice?.tournament?.player_limit)} ${t("secure.facility.order.invoices.invoice_edit.064")}`}
                      {" • "}
                      {invoice?.tournament?.date}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-md text-semibold">{invoice?.hospitality_event?.name}</p>
                    <p className="text-sm text-gray-500">{invoice?.hospitality_event?.date}</p>
                  </div>
                )
              ) : (
                <Select
                  showSearch
                  onSearch={(query: string) => setFilterState(prevState => ({ ...prevState, eventQuery: query }))}
                  onChange={(value: any, event: any) => handleEventChange(event)}
                  placeholder={t("secure.facility.order.invoices.invoice_edit.068")}
                  allowClear
                  searchValue={filterState.eventQuery}
                  showDropDownOnFocus={true}
                  searching={!events}
                >
                  <Option value={-1} extraValues={{ name: t("secure.facility.order.invoices.invoice_edit.069"), id: -1 }}>
                    <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_edit.069")}</div>
                  </Option>
                  {events?.map((event: any, index: number) => (
                    <Option value={event?.id} extraValues={event} key={index}>
                      <div className="text-semibold text-lg">{event.name}</div>
                    </Option>
                  ))}
                </Select>
              )}
            </Card.Section>
          </Card>
        </Page.Section>
      </Page>

      <AddFolderProductsModal
        open={addItemsOpen}
        title={t("Add items")}
        supportText={t("Search products to add to this invoice")}
        confirmText={t("secure.facility.order.invoices.invoice_edit.100")}
        showPrice
        showSlotUsage={false}
        hideSlots
        enableVariantSelection
        onClose={() => {
          setAddItemsOpen(false);
          addItemsPicker.resetPicker();
        }}
        onAdd={products => {
          void postLineItem(products);
        }}
        search={addItemsPicker.search}
        onSearchChange={addItemsPicker.setSearch}
        browseFilter={addItemsPicker.browseFilter}
        onBrowseFilterChange={addItemsPicker.setBrowseFilter}
        departments={addItemsPicker.departments}
        departmentsLoading={addItemsPicker.departmentsLoading}
        uncategorizedCount={addItemsPicker.uncategorizedCount}
        productsToAdd={addItemsPicker.productsToAdd}
        availableSlotCount={200}
        totalEmptySlotCount={200}
        folderProducts={[]}
        productSearchResults={addItemsPicker.productSearchResults}
        productSearchTotalCount={addItemsPicker.productSearchTotalCount}
        productSearchPending={addItemsPicker.productSearchPending}
        productSearchLoadingMore={addItemsPicker.productSearchLoadingMore}
        productSearchSelectAllPending={addItemsPicker.productSearchSelectAllPending}
        onLoadMoreProducts={addItemsPicker.loadMoreProducts}
        onSelectAllMatchingProducts={addItemsPicker.selectAllMatchingProducts}
        onSelectProduct={addItemsPicker.handleSelectProduct}
        onUnselectProduct={addItemsPicker.handleUnselectProduct}
        onCreateProduct={() => {
          setAddItemsOpen(false);
          addItemsPicker.resetPicker();
          history.push("/admin/product/new");
        }}
        onGoToProducts={() => {
          setAddItemsOpen(false);
          addItemsPicker.resetPicker();
          history.push("/admin/product");
        }}
      />

      <InvoiceCustomerDetailsModal
        open={customerDetailsOpen}
        customer={invoice?.customer}
        billingAddressId={invoice?.customer_billing_address_id}
        shippingAddressId={invoice?.customer_shipping_address_id}
        onCancel={() => setCustomerDetailsOpen(false)}
        onUpdate={saveCustomerDetailsFromModal}
      />

      {invoice && (paymentModalVisible || depositModalVisible) && (
        <InvoicePaymentModal
          key={depositModalVisible ? "deposit" : "payment"}
          paymentModalVisible={paymentModalVisible || depositModalVisible}
          invoice={invoice}
          paymentMethods={null} // TODO: Load current customer payment methods
          onOk={() => void loadInvoice()}
          closePaymentModal={() => {
            setPaymentModalVisible(false);
            setDepositModalVisible(false);
          }}
          customer={invoice?.customer}
          depositOnly={depositModalVisible}
          events={events}
          eventSearchValue={filterState.eventQuery}
          onEventSearch={(query: string) => setFilterState(prevState => ({ ...prevState, eventQuery: query }))}
        />
      )}

      <Popup
        open={removeLineItemState.modalVisible}
        title={t("secure.facility.order.invoices.invoice_edit.081")}
        description={t("secure.facility.order.invoices.invoice_edit.082")}
        type="warning"
        onCancel={() => setRemoveLineItemState({ modalVisible: false, itemId: null })}
        onOk={() => removeLineItem(removeLineItemState.itemId)}
      />

      <Popup
        open={finalizePopupVisible}
        title={t("secure.facility.order.invoices.invoice_edit.083")}
        description={t("secure.facility.order.invoices.invoice_edit.084")}
        type="warning"
        onCancel={() => setFinalizePopupVisible(false)}
        onOk={() => finalizeInvoice()}
      />

      <Popup
        open={applyDepositPopup.isOpen}
        title={t("secure.facility.order.invoices.invoice_edit.085")}
        description={t("secure.facility.order.invoices.invoice_edit.086")}
        type="warning"
        onCancel={() => closeApplyDepositPopup()}
        onOk={() => applyDeposit(Number(applyDepositPopup.depositId))}
      />

      <Popup
        open={removeDepositPopup.isOpen}
        title={t("secure.facility.order.invoices.invoice_edit.087")}
        description={t("secure.facility.order.invoices.invoice_edit.088")}
        type="warning"
        onCancel={() => closeRemoveDepositPopup()}
        onOk={() => removeDeposit(Number(removeDepositPopup.depositId))}
      />

      <Portal isMounted={voidInvoicePopup.isOpen}>
        <Popup
          open={voidInvoicePopup.isOpen}
          title={t("secure.facility.order.invoices.invoice_edit.089")}
          description={t("secure.facility.order.invoices.invoice_edit.090")}
          onOk={voidInvoice}
          okText={t("secure.facility.order.invoices.invoice_edit.091")}
          onCancel={closeVoidInvoicePopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={duplicateInvoicePopup.isOpen}>
        <Popup
          open={duplicateInvoicePopup.isOpen}
          title={t("secure.facility.order.invoices.invoice_edit.092")}
          description={t("secure.facility.order.invoices.invoice_edit.093")}
          onOk={duplicateInvoice}
          okText={t("secure.facility.order.invoices.invoice_edit.094")}
          onCancel={closeDuplicateInvoicePopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={editDetailsModal.isOpen}>
        <Sheet
          title={t("Edit Invoice Details")}
          open={editDetailsModal.isOpen}
          size="small"
          closable
          onCancel={closeEditDetailsModal}
          onOk={saveInvoiceDetailsFromModal}
          cancelText={t("Cancel")}
          okText={t("Update Changes")}
          overflow
        >
          <div className="invoice-details-edit-modal">
            <DatePickerInput
              label={t("Invoice Date")}
              months={1}
              position="left"
              startingDate={editDetailsModal.date ? new Date(moment(editDetailsModal.date).format()) : new Date()}
              setStartingDate={(date: Date) =>
                updateEditDetailsModal({ date: moment(date).format("YYYY-MM-DD") })
              }
            />
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label={t("Payment Terms")}
                    onChange={(value: string) => updateEditDetailsModal({ invoice_due: value })}
                    defaultValue={editDetailsModal.invoice_due}
                  >
                    <Option key={0} value={"on_receipt"}>
                      {t("On Receipt")}
                    </Option>
                    <Option key={1} value={"net_30"}>
                      {t("Net 30")}
                    </Option>
                    <Option key={2} value={"net_60"}>
                      {t("Net 60")}
                    </Option>
                  </Select>
                  <Select
                    label={t("Register")}
                    onChange={(value: number) => updateEditDetailsModal({ register_id: value })}
                    defaultValue={editDetailsModal.register_id ?? null}
                  >
                    {facilityStore?.registers?.map((register, registerIndex) => (
                      <Option key={registerIndex} value={register.id} name={register.title}>
                        {register.title}
                      </Option>
                    ))}
                  </Select>
                </FormLayout.Group>
              </FormLayout>
            </Form>
            <Input
              onChange={e => updateEditDetailsModal({ po_number: e.target.value })}
              value={editDetailsModal.po_number}
              label={t("PO Number")}
              id={"po_number"}
            />
            <div className="invoice-details-edit-modal__note">
              <TextEditor
                label={t("Note")}
                markdownText={editDetailsModal.notes}
                markdownTextOnChange={(value: string) => updateEditDetailsModal({ notes: value })}
              />
              <div className="invoice-details-edit-modal__note-actions">
                <ButtonNew size="small" type="secondary" onClick={() => updateEditDetailsModal({ notes: "" })}>
                  {t("Clear content")}
                </ButtonNew>
              </div>
            </div>
          </div>
        </Sheet>
      </Portal>
    </>
  );
}

// function generateStatusBadge(status: string) {
//   switch (status) {
//     case "draft":
//       return <Badge type="gray">{valueToString(status, "capitalize")}</Badge>;
//     case "open":
//       return <Badge type="primary">{valueToString(status, "capitalize")}</Badge>;
//     case "paid":
//       return <Badge type="success">{valueToString(status, "capitalize")}</Badge>;
//     case "partially_paid":
//       return <Badge type="warning">{valueToString(status, "capitalize")}</Badge>;
//     case "voided":
//       return <Badge type="error">{valueToString(status, "capitalize")}</Badge>;
//     default:
//       return <Badge type="gray">{valueToString(status, "capitalize")}</Badge>;
//   }
// }
